import { Badge } from '@viastud/ui/badge'
import { Button } from '@viastud/ui/button'
import { Card } from '@viastud/ui/card'
import { createDate } from '@viastud/ui/lib/date-service'
import { CheckCircle2, FileText } from 'lucide-react'
import type React from 'react'

interface Invoice {
  id: string
  subscriptionPlanName: string
  paidAt: string | Date | null
  amount: number
  status: string
  stripeInvoiceId?: string | null
}

interface Payment {
  id: string
  amount: number
  paidAt: string | Date | null
  status: string
}

interface BillingSectionProps {
  invoices: Invoice[]
  payments: Payment[]
  onDownload: (id: string) => void
}

export const BillingSection: React.FC<BillingSectionProps> = ({
  invoices,
  payments,
  onDownload,
}) => {
  return (
    <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
      {/* Sous-composant : Factures */}
      <Card className="flex flex-col p-6">
        <div className="mb-6 flex items-center justify-between">
          <h3 className="text-xl font-bold text-gray-900">Factures</h3>
          {/*<Button variant="ghost" className="h-auto p-0 font-semibold text-blue-600">
            Historique complet
          </Button>*/}
        </div>
        <div className="flex flex-col gap-4">
          {invoices.length > 0 ? (
            invoices.map((invoice) => (
              <div
                key={invoice.id}
                className="flex items-center justify-between border-b border-gray-100 pb-4 last:border-0 last:pb-0"
              >
                <div className="flex flex-col gap-1">
                  <span className="font-semibold text-gray-900">
                    {invoice.subscriptionPlanName}
                  </span>
                  <span className="text-sm text-gray-500">
                    {invoice.paidAt ? createDate(invoice.paidAt).format('DD/MM/YYYY') : 'N/A'}
                  </span>
                </div>
                <div className="flex items-center gap-4">
                  <Badge
                    className={
                      invoice.status === 'paid'
                        ? 'bg-green-100 text-green-700 hover:bg-green-100'
                        : 'bg-red-100 text-red-700 hover:bg-red-100'
                    }
                  >
                    {invoice.status === 'paid' ? 'Payée' : 'Échouée'}
                  </Badge>
                  <span className="font-bold text-gray-900">
                    {invoice.amount.toFixed(2).replace('.', ',')}€
                  </span>
                  <Button
                    variant="icon"
                    size="icon"
                    onClick={() => {
                      if (invoice.stripeInvoiceId) onDownload(invoice.stripeInvoiceId)
                    }}
                    disabled={!invoice.stripeInvoiceId}
                    className="text-gray-400 hover:text-blue-600"
                  >
                    <FileText className="h-5 w-5" />
                  </Button>
                </div>
              </div>
            ))
          ) : (
            <p className="italic text-gray-500">Aucune facture disponible</p>
          )}
        </div>
      </Card>

      {/* Sous-composant : Paiements */}
      <Card className="flex flex-col p-6">
        <div className="mb-6 flex items-center justify-between">
          <h3 className="text-xl font-bold text-gray-900">Paiements</h3>
          {/*<Button variant="ghost" className="h-auto p-0 font-semibold text-blue-600">
            Historique complet
          </Button>*/}
        </div>
        <div className="flex flex-col gap-4">
          {payments.length > 0 ? (
            payments.map((payment) => (
              <div
                key={payment.id}
                className="flex items-center justify-between border-b border-gray-100 pb-4 last:border-0 last:pb-0"
              >
                <div className="flex items-center gap-3">
                  <div
                    className={
                      payment.status === 'succeeded'
                        ? 'flex h-8 w-8 items-center justify-center rounded-full bg-green-50 text-green-600'
                        : payment.status === 'cancelled'
                          ? 'flex h-8 w-8 items-center justify-center rounded-full bg-gray-50 text-gray-600'
                          : 'flex h-8 w-8 items-center justify-center rounded-full bg-red-50 text-red-600'
                    }
                  >
                    <CheckCircle2 className="h-5 w-5" />
                  </div>
                  <div className="flex flex-col">
                    <span className="font-bold text-gray-900">
                      {payment.amount.toFixed(2).replace('.', ',')}€
                    </span>
                    <span className="text-xs text-gray-500">
                      {payment.paidAt
                        ? createDate(payment.paidAt).format('DD/MM/YYYY HH:mm')
                        : 'N/A'}
                    </span>
                  </div>
                </div>
                <Badge
                  className={
                    payment.status === 'succeeded'
                      ? 'bg-green-100 text-green-700 hover:bg-green-100'
                      : payment.status === 'cancelled'
                        ? 'bg-gray-100 text-gray-700 hover:bg-gray-100'
                        : 'bg-red-100 text-red-700 hover:bg-red-100'
                  }
                >
                  {payment.status === 'succeeded'
                    ? 'Réussi'
                    : payment.status === 'cancelled'
                      ? 'Annulé'
                      : 'Échoué'}
                </Badge>
              </div>
            ))
          ) : (
            <p className="italic text-gray-500">Aucun paiement disponible</p>
          )}
        </div>
      </Card>
    </div>
  )
}
